/* eslint-disable no-await-in-loop */
import {SpeedApiSdk} from 'services/callApi'
import {WS_ROOT} from 'constants'
import {WebSocketClient} from '@binance/js-sdk'

const EXCHANGE_TIMEZONE = 'Asia/Shanghai'

const TV_RESOLUTIONS_TO_BINANCE = {
  // tradingViewFormat: binanceFormat
  1: '1m',
  3: '3m',
  5: '5m',
  15: '15m',
  30: '30m',
  60: '1h',
  120: '2h',
  240: '4h',
  360: '6h',
  720: '12h',
  D: '1d',
  '1W': '1w',
  '1M': '1M'
}

const klineInterval = {'1D': '1d', '1W': '1w'}
const getKlinesWsUri = (symbolName, interval) =>
  `${WS_ROOT}?streams=${symbolName.toLowerCase()}@kline_${klineInterval[interval] || interval}`

const convertKlineObjToBar = ({k: kline}) => {
  const {
    t, l, c, h, o, v
  } = kline
  return {
    time: t,
    close: +c,
    open: +o,
    high: +h,
    low: +l,
    volume: +v
  }
}

const translateResolutionForBinance = resolution => TV_RESOLUTIONS_TO_BINANCE[resolution]

export default class DataFeed {
  constructor (tickSize) {
    this.tickSize = tickSize
    this.sockets = new Map()
  }

  // ### onReady(callback)
  // `callback`: function(configurationData)
  //     `configurationData`: object (see below)
  //          exchanges
  //          symbols_types
  //          supported_resolutions
  //          supports_marks
  //          supports_timescale_marks
  //          supports_time
  //          futures_regex
  onReady = callback => {
    setTimeout(() => {
      callback({
        exchanges: [],
        symbols_types: [],
        supported_resolutions: Object.keys(TV_RESOLUTIONS_TO_BINANCE),
        supports_marks: false, // TODO: maybe
        supports_timescale_marks: false, // TODO: maybe
        supports_time: true
      })
    }, 0)
  }

  // ### searchSymbols(userInput, exchange, symbolType, onResultReadyCallback)
  // `userInput`: string. It is text entered by user in the symbol search field.
  // `exchange`: string. The requested exchange (chosen by user).
  //     Empty value means no filter was specified.
  // `symbolType`: string. The requested symbol type: `index`, `stock`, `forex`, etc.
  //     Empty value means no filter was specified.
  // `onResultReadyCallback`: function(result)
  //     `result`: array (see below)
  //        "symbol": "<short symbol name>",
  //        "full_name": "<full symbol name>", // e.g. BTCE:BTCUSD
  //        "description": "<symbol description>",
  //        "exchange": "<symbol exchange name>",
  //        "ticker": "<symbol ticker name, optional>",
  //        "type": "stock" // or "futures" or "crypto" or "forex" or "index"
  searchSymbols = () => {}

  // ### resolveSymbol(symbolName, onSymbolResolvedCallback, onResolveErrorCallback)
  // `symbolName`: string. Symbol name or `ticker` if provided.
  // `onSymbolResolvedCallback`: function([SymbolInfo](Symbology#symbolinfo-structure))
  // `onResolveErrorCallback`: function(reason)
  // Charting Library calls this fn when it needs [SymbolInfo](Symbology#symbolinfo-structure)
  // by symbol name.
  resolveSymbol = (symbol, callback) => {
    const {tickSize} = this
    const resolved = {
      description: symbol,
      fractional: false, // `fractional` for common prices is `false` or it can be skipped.
      has_intraday: true,
      minmov: 1, // `minmov` is a number of units that make up one tick.
      minmove2: 0, // `minmove2` for common prices is `0` or it can be skipped.
      name: symbol,
      // `pricescale` defines number of decimal places.
      // Actually it is `10^number-of-decimal-places`.
      pricescale: 10 ** Math.abs(Math.log10(tickSize)),
      session: '24x7',
      timezone: EXCHANGE_TIMEZONE,
      type: 'crypto'
      // volume_precision: 2,
    }
    // TODO: fill this out properly
    setTimeout(() => {
      callback(resolved)
    }, 0)
  }

  // ### getBars(symbolInfo, resolution, from, to, onHistoryCallback,
  //              onErrorCallback, firstDataRequest)
  // `symbolInfo`: [SymbolInfo](Symbology#symbolinfo-structure) object
  // `resolution`: string
  // `from`: unix timestamp, leftmost required bar time
  // `to`: unix timestamp, rightmost required bar time
  // `onHistoryCallback`: function(array of `bar`s, `meta` = `{ noData = false }`)
  //     `bar`: object `{time, close, open, high, low, volume}`
  //     `meta`: object `{noData = true | false, nextTime - unix time}`
  // `onErrorCallback`: function(reason)
  // `firstDataRequest`: boolean to identify the first call of this method for the
  //     particular symbol resolution.
  //     When it is set to `true` you can ignore `to` (which depends on browser's `Date.now()`)
  //     and return bars up to the latest bar.
  getBars = async ({name: symbol}, resolution, from, to, callback, onError) => {
    const interval = translateResolutionForBinance(resolution)
    // this api is hard limited to return 1000 bars max, regardless of `limit`
    const limit = 1500 // never more than 1000!
    const getParams = startTime => ({
      symbol,
      interval,
      limit,
      startTime,
      endTime: to * 1000
    })
    let lastCount = -1
    let lastTime = from * 1000
    let errored = false
    const bars = []
    // deal with the 1000 hard limit by making multiple requests
    while (lastCount === -1 || lastCount === limit) {
      try {
        const isFirst = lastCount === -1
        // +1 to not get duplicate bars
        const params = getParams(isFirst ? lastTime : Number(lastTime) + 1)
        const {klines} = await SpeedApiSdk.futures.getKline(params)
        if (!klines || Object.keys(klines).length === 0) break
        const keys = Object.keys(klines)
        const lastIdx = keys.length - 1
        const time = keys[lastIdx]
        lastTime = time
        lastCount = keys.length + 1

        keys.map(k =>
          bars.push({
            time: +k,
            close: klines[k].close,
            open: klines[k].open,
            high: klines[k].high,
            low: klines[k].low,
            volume: klines[k].volume
          })
        )
      } catch (err) {
        errored = true
        onError(err)
        break
      }
    }
    if (!errored) {
      callback(bars, {noData: !bars.length})
    }
  }

  // ### subscribeBars(symbolInfo, resolution, onRealtimeCallback, subscriberUID,
  //                    onResetCacheNeededCallback)
  // Charting Library calls this function when it wants to receive real-time updates for a symbol.
  // The Library assumes that you will call `onRealtimeCallback` every time you want to update
  // the most recent bar or to add a new one.
  // `symbolInfo`: [SymbolInfo](Symbology#symbolinfo-structure) object
  // `resolution`: string
  // `onRealtimeCallback`: function(bar)
  //     `bar`: object `{time, close, open, high, low, volume}`
  // `subscriberUID`: object
  // `onResetCacheNeededCallback` *(since version 1.7)*: function() to be executed
  //    when bar data has changed
  subscribeBars = async ({name: symbol}, resolution, onRealtimeCallback, uid, onResetNeeded) => {
    if (this._lastBarsSymbol && this._lastBarsSymbol !== symbol) {
      onResetNeeded()
      return
    }
    this._lastBarsSymbol = symbol
    const interval = translateResolutionForBinance(resolution)
    const klinesWsUri = getKlinesWsUri(symbol, interval)
    const _ws = WebSocketClient(klinesWsUri)
    _ws.onmessage(evt => {
      const {data} = JSON.parse(evt.data)
      if (data) {
        onRealtimeCallback(convertKlineObjToBar(data))
      }
    })
    // _ws.onopen(() => {
    //   onResetNeeded()
    // })
    _ws.open()
    this.sockets.set(uid, _ws)
  }

  // ### unsubscribeBars(subscriberUID)
  // `subscriberUID`: object
  // Charting Library calls this function when it doesn't want to receive updates for
  // this subscriber any more. `subscriberUID` will be the same object that Library passed
  // to `subscribeBars` before.
  unsubscribeBars = uid => {
    const socket = this.sockets.get(uid)
    if (!socket) {
      return
    }
    socket.close(1e3, 'Requested by TradingView')
  }

  // ### calculateHistoryDepth(resolution, resolutionBack, intervalBack)
  // *Optional.*
  // `resolution`: requested symbol resolution
  // `resolutionBack`: time period types. Supported values are: `D` | `M`
  // `intervalBack`: amount of `resolutionBack` periods that the Charting Library is
  // going to request
  // calculateHistoryDepth = (resolution, resolutionBack, intervalBack) => ({
  //   resolutionBack,
  //   // 1000 bars hard limit on the API.
  //   intervalBack: intervalBack > 1000 ? 999 : intervalBack,
  // });

  // ### getServerTime(callback)
  // This function is called if the configuration flag `supports_time` is set to `true` when
  // the Charting Library needs to know the server time.
  // `callback`: function(unixTime)
  getServerTime = async callback => {
    try {
      const {serverTime} = await SpeedApiSdk.futures.getServerTime()
      const secs = Math.floor(parseInt(serverTime, 10) / 1000)
      callback(secs)
    } catch (err) {
      callback(Date.now())
    }
  }
}
